import { useEffect, useMemo, useState } from "react";
import type { Channel } from "@hybrid-iptv/types";
import { Dialog, Spinner } from "@hybrid-iptv/ui";
import { nativeGuideChannels, nativeGuideNow } from "../api/nativeGuide.js";
import { useSettings } from "../settings/settingsStore.js";
import { providerBody } from "./api.js";
import { choiceOf, rankChoices, suggestedQuery, type GuideChoice } from "./chooseGuide.js";
import { feedLabel } from "./guideFeeds.js";
import { guideNameKey, useGuideMap } from "./guideMapStore.js";
import { ShiftStepper } from "./ShiftStepper.js";

const OWN_LABEL = "This playlist's guide";

/**
 * "Choose guide" (Phase 3 step 4, docs/build-guide-playlists-epg.md §5), from a
 * channel's hold-OK menu: pin the channel to a guide channel of its playlist's
 * own guide or one of its switched-on extra sources, or give it back to the
 * automatic match; and shift its times. Saved on this TV, per playlist, by the
 * channel's name, so every listing of the channel takes it. The TV app's only.
 *
 * Automatic comes first, so the dialog opens on it; the shift is next, above the
 * search, so it is two presses away however long the list is.
 */
export function ChooseGuideDialog({ sourceId, channel, onClose }: { sourceId: string; channel: Channel; onClose: () => void }) {
  const provider = useSettings((s) => s.providers.find((p) => p.id === sourceId));
  const key = guideNameKey(channel.name);
  const pin = useGuideMap((s) => s.pins[sourceId]?.[key]);
  const ownShift = useGuideMap((s) => s.shifts[sourceId]?.[key]);
  const setPin = useGuideMap((s) => s.setPin);
  const setShift = useGuideMap((s) => s.setShift);
  const [choices, setChoices] = useState<GuideChoice[] | null>(null);
  const [query, setQuery] = useState(() => suggestedQuery(channel.name));
  const [nowOn, setNowOn] = useState<ReadonlyMap<string, string>>(new Map());

  const feeds = useMemo(() => (provider?.guideFeeds ?? []).filter((feed) => feed.enabled), [provider?.guideFeeds]);

  // Every source's guide channels, read once per opening.
  useEffect(() => {
    if (!provider) return;
    let cancelled = false;
    void (async () => {
      const { deviceOwnGuideUrl } = await import("../api/deviceApi.js");
      const own = await deviceOwnGuideUrl(providerBody(provider)).catch(() => null);
      const sources = [
        ...(own ? [{ feed: "own", label: OWN_LABEL, url: own }] : []),
        ...feeds.map((feed) => ({ feed: feed.id, label: feedLabel(feed.url), url: feed.url })),
      ];
      const all: GuideChoice[] = [];
      for (const source of sources) {
        const listed = await nativeGuideChannels(source.url).catch(() => null);
        for (const entry of listed ?? []) all.push(choiceOf(source.feed, source.label, source.url, entry.id, entry.displayName));
      }
      if (!cancelled) setChoices(all);
    })();
    return () => {
      cancelled = true;
    };
    // Read once per opening: a validation stamp on the playlist must not read them all again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, feeds]);

  const results = useMemo(() => (choices ? rankChoices(choices, query) : []), [choices, query]);

  // What is on now, for the results shown.
  useEffect(() => {
    if (results.length === 0) return;
    let cancelled = false;
    void (async () => {
      const byUrl = new Map<string, string[]>();
      for (const choice of results) byUrl.set(choice.url, [...(byUrl.get(choice.url) ?? []), choice.guideChannelId]);
      const next = new Map<string, string>();
      for (const [url, ids] of byUrl) {
        const titles = await nativeGuideNow(url, ids).catch(() => new Map<string, string>());
        for (const [id, title] of titles) next.set(`${url}\n${id}`, title);
      }
      if (!cancelled) setNowOn(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [results]);

  const pinnedFeed = pin ? (pin.feed === "own" ? null : feeds.find((feed) => feed.id === pin.feed)) : null;
  // A pin to a source switched off or removed is not used (guideTuning): the channel is automatic.
  const pinInUse = pin && (pin.feed === "own" || pinnedFeed) ? pin : null;
  const sourceShift = (pinnedFeed ? pinnedFeed.shiftMinutes : provider?.guideShiftMinutes) ?? 0;
  const inherited = channel.epgShiftMinutes ?? sourceShift;
  const shown = ownShift ?? inherited;
  const note = ownShift !== undefined ? undefined : channel.epgShiftMinutes !== undefined ? "(from the playlist)" : "(the guide's own)";

  return (
    <Dialog open title={`Guide for ${channel.name}`} onClose={onClose}>
      <p className="choose-guide__state">
        {pinInUse
          ? `Pinned to ${pinInUse.guideName ?? pinInUse.guideChannelId} · ${pinnedFeed ? feedLabel(pinnedFeed.url) : OWN_LABEL}`
          : "Automatic: matched by the channel's name"}
      </p>
      <div className="choose-guide__list">
        <button type="button" className="choose-guide__choice" onClick={() => setPin(sourceId, channel.name, null)}>
          <span className="choose-guide__name">Automatic</span>
          {!pinInUse && <span className="choose-guide__used">✓ In use</span>}
        </button>
      </div>
      <ShiftStepper
        minutes={shown}
        note={note}
        onChange={(minutes) => setShift(sourceId, channel.name, minutes === inherited ? null : minutes)}
      />
      <input
        className="mirror-picker__search choose-guide__search"
        type="search"
        placeholder="Search the guide's channels…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search the guide's channels"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
      />
      <div className="choose-guide__list choose-guide__results">
        {choices === null && <Spinner />}
        {choices !== null && choices.length === 0 && <p className="mirror-picker__empty">This playlist's guide has not downloaded yet.</p>}
        {choices !== null && choices.length > 0 && results.length === 0 && (
          <p className="mirror-picker__empty">{query.trim() ? "No guide channels match." : "Type a channel's name."}</p>
        )}
        {results.map((choice) => {
          const used = pinInUse?.feed === choice.feed && pinInUse.guideChannelId === choice.guideChannelId;
          const now = nowOn.get(`${choice.url}\n${choice.guideChannelId}`);
          return (
            <button
              key={`${choice.feed}\n${choice.guideChannelId}`}
              type="button"
              className="choose-guide__choice"
              onClick={() => setPin(sourceId, channel.name, { feed: choice.feed, guideChannelId: choice.guideChannelId, guideName: choice.name })}
            >
              <span className="choose-guide__name">{choice.name}</span>
              <span className="choose-guide__meta">
                {choice.feedLabel}
                {now ? ` · Now: ${now}` : ""}
              </span>
              {used && <span className="choose-guide__used">✓ In use</span>}
            </button>
          );
        })}
      </div>
    </Dialog>
  );
}
